// 使用モデル / 推論プリセット セレクター — チャットと日報AI下書きで共用。
// 表示されるのは「現在ロード済みのモデル」のみ。選択はサーバー側に
// ユーザー単位 + 会話/下書き単位で保存され、AI機能ルーティング（管理者の
// 既定値）には一切書き込まない。ローカルLLM以外（クラウド実行時）は
// selectable=false が返り、コンポーネントは何も描画しない。
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Cpu, SlidersHorizontal, WarningCircle, CircleNotch, ArrowsClockwise } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MSG_NO_MODELS = '現在利用可能なテキスト生成モデルがありません。AI管理者にモデルの読み込み状況を確認してください。';
const MSG_PREV_UNLOADED = '以前使用したモデルは現在読み込まれていません。現在利用可能なモデルを選択してください。';

export default function ModelPresetPicker({ feature, instanceId, onChange, compact = false, disabled = false, wrapperClassName = '' }) {
  const [inv, setInv] = useState(null);
  const [presets, setPresets] = useState([]);
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const seqRef = useRef(0);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const notify = useCallback((s, i) => {
    const selectable = i ? i.selectable !== false : true;
    onChangeRef.current?.({
      selectable,
      modelId: s?.model_id || null,
      presetId: s?.preset_id || null,
      ready: !selectable || !!(s?.model_id && s?.model_available !== false),
    });
  }, []);

  const load = useCallback(async (refresh = false) => {
    const seq = ++seqRef.current;
    setLoading(true);
    try {
      const [mi, pr, se] = await Promise.all([
        axios.get(`${API}/ai/runtime/models`, { params: { feature, ...(refresh ? { refresh: 1 } : {}) } }),
        axios.get(`${API}/ai/runtime/presets`, { params: { feature } }),
        axios.get(`${API}/ai/runtime/selection`, { params: { feature, instance_id: instanceId || '' } }),
      ]);
      if (seq !== seqRef.current) return;
      setInv(mi.data);
      setPresets(pr.data.presets || []);
      setSel(se.data);
      setLoadError(false);
      notify(se.data, mi.data);
    } catch {
      // 一時的な取得失敗では選択をリセットしない（前回値を保持）
      if (seq === seqRef.current) setLoadError(true);
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [feature, instanceId, notify]);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    const seq = ++seqRef.current; // 古いレスポンスが新しい選択を上書きしないように
    const next = { ...sel, ...patch, model_available: true, saved_model_unavailable: false };
    setSel(next);
    notify(next, inv);
    try {
      await axios.patch(`${API}/ai/runtime/selection`, {
        feature,
        instance_id: instanceId || null,
        model_id: next.model_id || null,
        preset_id: next.preset_id || null,
      });
    } catch (e) {
      if (seq !== seqRef.current) return;
      // 保存失敗（モデルが直前にアンロードされた等）→ 再取得して実状態に同期
      load();
    }
  };

  if (inv && inv.selectable === false) return null; // クラウド実行時は選択UIなし
  if (loading && !inv) {
    return (
      <div className={wrapperClassName} data-testid={`ai-picker-loading-${feature}`}>
        <p className="text-[10px] text-zinc-400 inline-flex items-center gap-1">
          <CircleNotch size={11} className="animate-spin" /> 利用可能なモデルを確認中…
        </p>
      </div>
    );
  }
  if (!inv) {
    if (!loadError) return null;
    return (
      <div className={wrapperClassName} data-testid={`ai-picker-error-${feature}`}>
        <p className="text-[10px] text-amber-600 inline-flex items-center gap-1">
          <WarningCircle size={11} /> モデル一覧を取得できませんでした
          <button type="button" onClick={() => load(true)} className="underline hover:text-amber-800">再試行</button>
        </p>
      </div>
    );
  }

  const models = inv.models || [];
  const noModels = models.length === 0;
  const currentModel = sel?.model_id || '';
  const currentUnlisted = !!currentModel && !models.some((m) => m.id === currentModel);
  const labelCls = 'text-[10px] font-bold text-zinc-500 uppercase tracking-wider';
  const trigCls = compact ? 'h-7 text-[11px] bg-white min-w-[150px] max-w-[220px]' : 'h-9 text-xs bg-white';

  return (
    <div className={wrapperClassName} data-testid={`ai-picker-${feature}`}>
      <div className={compact ? 'flex flex-wrap items-center gap-2' : 'grid grid-cols-1 sm:grid-cols-2 gap-3'}>
        <div className={compact ? 'flex items-center gap-1.5' : 'space-y-1'}>
          {compact
            ? <Cpu size={13} className="text-violet-500 shrink-0" title="使用モデル" />
            : <label className={labelCls}>使用モデル</label>}
          <Select value={currentModel || '__none__'} disabled={disabled || noModels}
            onValueChange={(v) => v !== '__none__' && save({ model_id: v })}>
            <SelectTrigger className={trigCls} data-testid={`ai-model-select-${feature}`} aria-label="使用モデル">
              <SelectValue placeholder={noModels ? 'モデルなし' : 'モデルを選択'} />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {currentUnlisted && (
                <SelectItem value={currentModel} disabled>
                  {currentModel} <span className="text-[10px] text-rose-500">(未ロード)</span>
                </SelectItem>
              )}
              {models.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                    {m.display_name}
                    {m.context_length ? <span className="text-[9px] text-zinc-400">{Math.round(m.context_length / 1024)}k</span> : null}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className={compact ? 'flex items-center gap-1.5' : 'space-y-1'}>
          {compact
            ? <SlidersHorizontal size={13} className="text-violet-500 shrink-0" title="推論プリセット" />
            : <label className={labelCls}>推論プリセット</label>}
          <Select value={sel?.preset_id || '__none__'} disabled={disabled || noModels}
            onValueChange={(v) => save({ preset_id: v === '__none__' ? null : v, preset_name: null })}>
            <SelectTrigger className={trigCls} data-testid={`ai-preset-select-${feature}`} aria-label="推論プリセット">
              <SelectValue placeholder="プリセット" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="__none__">既定（管理設定）</SelectItem>
              {presets.map((p) => (
                <SelectItem key={p.id} value={p.id} textValue={p.name_ja} title={p.description_ja || ''}>
                  {p.name_ja}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {compact && (
          <button type="button" onClick={() => load(true)} title="モデル一覧を更新"
            data-testid={`ai-picker-refresh-${feature}`}
            className="p-1 rounded text-zinc-300 hover:text-violet-600 hover:bg-violet-50 transition-colors">
            <ArrowsClockwise size={12} className={loading ? 'animate-spin' : ''} />
          </button>
        )}
      </div>
      {sel?.saved_model_unavailable && (
        <p className="mt-1 text-[10px] text-amber-600 inline-flex items-start gap-1" data-testid={`ai-picker-unavailable-${feature}`}>
          <WarningCircle size={11} className="mt-px shrink-0" /> {MSG_PREV_UNLOADED}
          {sel.saved_model_id && <span className="font-mono text-[9px] text-amber-500">（{sel.saved_model_id}）</span>}
        </p>
      )}
      {noModels && (
        <p className="mt-1 text-[10px] text-rose-600 inline-flex items-start gap-1" data-testid={`ai-picker-empty-${feature}`}>
          <WarningCircle size={11} className="mt-px shrink-0" /> {MSG_NO_MODELS}
        </p>
      )}
      {loadError && (
        <p className="mt-1 text-[10px] text-amber-600" data-testid={`ai-picker-stale-${feature}`}>
          最新のモデル状況を取得できませんでした（前回の選択を表示中）
          <button type="button" onClick={() => load(true)} className="ml-1 underline hover:text-amber-800">再試行</button>
        </p>
      )}
    </div>
  );
}
